import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import { Eye, Edit, Trash2, Search, Plus, Truck, Loader2, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import { TransferFormDialog } from "@/components/mcf/TransferFormDialog";
import { TransferViewDialog } from "@/components/mcf/TransferViewDialog";
import { GrnUpdateDialog } from "@/components/mcf/GrnUpdateDialog";
import { useMcfLookups } from "@/components/mcf/useMcfLookups";
import { currentUserIsAdmin, errMsg, fmtDate, fmtQty, STATUS_STYLES } from "@/components/mcf/mcfUtils";

const PAGE_SIZE = 50;
const ALL = "__all__";
const HEADS = ["Invoice No.", "Invoice Date", "Pickup Date", "Sent Units", "GRN Units", "Status", "Pickup Location", "Drop Location", "Drop City", "Drop State", "Courier Partner", "Actions"];

function FilterSelect({ value, onChange, placeholder, options, testId, width = "w-[170px]" }) {
  return (
    <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? "" : v)}>
      <SelectTrigger className={width} data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{placeholder}</SelectItem>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function StatusBadge({ row }) {
  return <span className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${STATUS_STYLES[row.status]}`} data-testid={`mcf-row-status-${row.id}`}>{row.status}</span>;
}

export default function McfStockTransfers() {
  const isAdmin = currentUserIsAdmin();
  const lookups = useMcfLookups();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total_records: 0, total_pages: 0 });
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [filters, setFilters] = useState({ status: "", pickup_location: "", drop_warehouse_id: "", invoice_from: "", invoice_to: "", pickup_from: "", pickup_to: "" });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [grnTarget, setGrnTarget] = useState(null);
  const reqSeq = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => setPage(1), [debounced, filters]);

  const load = useCallback(async () => {
    const seq = ++reqSeq.current;
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE, ...(debounced && { search: debounced }), ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) };
      const res = await axios.get(`${API}/mcf/transfers`, { params });
      if (seq !== reqSeq.current) return;
      setRows(res.data.items);
      setMeta(res.data);
    } catch (err) {
      if (seq === reqSeq.current) toast.error(errMsg(err, "Could not load stock transfers"));
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, [page, debounced, filters]);
  useEffect(() => { load(); }, [load]);

  const fetchFull = async (id) => (await axios.get(`${API}/mcf/transfers/${id}`)).data;
  const openView = async (r) => { try { setViewing(await fetchFull(r.id)); } catch (e) { toast.error(errMsg(e, "Could not load transfer")); } };
  const openEdit = async (r) => { try { setEditing(await fetchFull(r.id)); setFormOpen(true); } catch (e) { toast.error(errMsg(e, "Could not load transfer")); } };

  const openGrn = async (r) => { try { setGrnTarget(await fetchFull(r.id)); } catch (e) { toast.error(errMsg(e, "Could not load transfer")); } };

  const doDelete = async () => {
    try {
      await axios.delete(`${API}/mcf/transfers/${deleting.id}`);
      toast.success(`Stock transfer ${deleting.invoice_number} deleted`);
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(errMsg(err, "Could not delete transfer"));
    }
  };

  const setF = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const hasFilters = debounced || Object.values(filters).some(Boolean);

  return (
    <div className="space-y-6" data-testid="mcf-stock-transfers-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold text-[#0F0F0F]" data-testid="mcf-page-title">MCF Stock Transfers</h1>
          <p className="text-muted-foreground mt-1">Track stock moved from Hub GGN to MCF warehouses, with SKU-wise GRN</p>
        </div>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-[#1F8F88] hover:bg-[#197a74] text-white" data-testid="mcf-add-transfer-button">
          <Plus className="h-4 w-4 mr-2" /> Add Stock Transfer
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle data-testid="mcf-total-count">All Stock Transfers ({meta.total_records})</CardTitle></CardHeader>
        <CardContent>
          <div className="sticky top-0 z-10 bg-white pb-4 mb-4 border-b flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search Invoice No., MCF Code, warehouse, courier..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="mcf-search-input" />
            </div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">Invoice
              <DateRangeFilter from={filters.invoice_from} to={filters.invoice_to} onChange={(f, t) => setF({ invoice_from: f, invoice_to: t })} testId="mcf-invoice-date-filter" />
            </div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">Pickup
              <DateRangeFilter from={filters.pickup_from} to={filters.pickup_to} onChange={(f, t) => setF({ pickup_from: f, pickup_to: t })} testId="mcf-pickup-date-filter" />
            </div>
            <FilterSelect value={filters.status} onChange={(v) => setF({ status: v })} placeholder="All Statuses" width="w-[180px]" testId="mcf-status-filter" options={["In-Transit", "Partially Delivered", "Delivered"].map((v) => ({ value: v, label: v }))} />
            <FilterSelect value={filters.pickup_location} onChange={(v) => setF({ pickup_location: v })} placeholder="All Pickup Locations" testId="mcf-pickup-filter" options={[...new Set([...lookups.pickupWarehouses.map((w) => w.warehouse_name), ...lookups.pickups])].map((p) => ({ value: p, label: p }))} />
            <FilterSelect value={filters.drop_warehouse_id} onChange={(v) => setF({ drop_warehouse_id: v })} placeholder="All Drop Locations" testId="mcf-drop-filter" options={lookups.dropWarehouses.map((w) => ({ value: w.id, label: `${w.warehouse_name} (${w.warehouse_code})` }))} />
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={() => { setSearch(""); setFilters({ status: "", pickup_location: "", drop_warehouse_id: "", invoice_from: "", invoice_to: "", pickup_from: "", pickup_to: "" }); }} data-testid="mcf-clear-filters">Clear</Button>
            )}
          </div>

          <div className="overflow-x-auto rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>{HEADS.map((h) => <TableHead key={h} className={`bg-slate-100 border-b-2 border-slate-200 whitespace-nowrap ${["Sent Units", "GRN Units"].includes(h) ? "text-right" : ""} ${h === "Actions" ? "text-right" : ""}`}>{h}</TableHead>)}</TableRow>
              </TableHeader>
              <TableBody>
                {loading && rows.length === 0 ? (
                  <TableRow><TableCell colSpan={12} className="text-center py-10 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading stock transfers…</TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={12} className="text-center py-12" data-testid="mcf-empty-state">
                    <Truck className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                    <p className="font-medium">{hasFilters ? "No transfers match these filters" : "No stock transfers yet"}</p>
                    <p className="text-sm text-muted-foreground">{hasFilters ? "Try clearing search or filters." : "Click Add Stock Transfer and upload the invoice to begin."}</p>
                  </TableCell></TableRow>
                ) : rows.map((r) => (
                  <TableRow key={r.id} className={`hover:bg-slate-50 transition-colors ${loading ? "opacity-60" : ""}`} data-testid={`mcf-row-${r.id}`}>
                    <TableCell className="font-medium whitespace-nowrap" data-testid={`mcf-row-invoice-${r.id}`}>{r.invoice_number}</TableCell>
                    <TableCell className="whitespace-nowrap">{fmtDate(r.invoice_date)}</TableCell>
                    <TableCell className="whitespace-nowrap">{fmtDate(r.pickup_date)}</TableCell>
                    <TableCell className="text-right" data-testid={`mcf-row-sent-${r.id}`}>{fmtQty(r.sent_units)}</TableCell>
                    <TableCell className="text-right" data-testid={`mcf-row-grn-${r.id}`}>
                      {fmtQty(r.grn_units)}
                      {r.pending_grn_units > 0 && <span className="block text-[10px] text-amber-700" data-testid={`mcf-row-pending-${r.id}`}>{fmtQty(r.pending_grn_units)} pending</span>}
                      {r.excess_grn_units > 0 && <span className="block text-[10px] text-rose-700" data-testid={`mcf-row-excess-${r.id}`}>{fmtQty(r.excess_grn_units)} excess</span>}
                    </TableCell>
                    <TableCell><StatusBadge row={r} /></TableCell>
                    <TableCell className="whitespace-nowrap">{r.pickup_location}{r.pickup_warehouse_code && <span className="font-mono text-xs text-muted-foreground"> ({r.pickup_warehouse_code})</span>}</TableCell>
                    <TableCell className="whitespace-nowrap">{r.drop_location_name} <span className="font-mono text-xs text-muted-foreground">({r.drop_mcf_code})</span></TableCell>
                    <TableCell>{r.drop_city}</TableCell>
                    <TableCell>{r.drop_state}</TableCell>
                    <TableCell>{r.courier_partner || "—"}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => openView(r)} title="View" data-testid={`mcf-view-${r.id}`}><Eye className="h-4 w-4" /></Button>
                      {isAdmin && <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-[#1F8F88]" onClick={() => openGrn(r)} title="Update GRN" data-testid={`mcf-grn-${r.id}`}><PackageCheck className="h-4 w-4" /></Button>}
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => openEdit(r)} title="Edit" data-testid={`mcf-edit-${r.id}`}><Edit className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-rose-600" onClick={() => setDeleting(r)} title="Delete" data-testid={`mcf-delete-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {meta.total_records > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-2 py-3 border-t border-slate-100 mt-1" data-testid="mcf-pagination">
              <p className="text-sm text-muted-foreground">Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, meta.total_records)} of {meta.total_records}</p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="h-8" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading} data-testid="mcf-pagination-prev">Previous</Button>
                <span className="text-sm px-2">Page {page} of {Math.max(1, meta.total_pages)}</span>
                <Button variant="outline" size="sm" className="h-8" onClick={() => setPage((p) => (p < meta.total_pages ? p + 1 : p))} disabled={page >= meta.total_pages || loading} data-testid="mcf-pagination-next">Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <TransferFormDialog open={formOpen} transfer={editing} lookups={lookups} isAdmin={isAdmin} onClose={() => setFormOpen(false)} onSaved={() => { setFormOpen(false); load(); }} />
      <TransferViewDialog transfer={viewing} onClose={() => setViewing(null)} onUpdateGrn={isAdmin ? () => { setGrnTarget(viewing); setViewing(null); } : null} />
      <GrnUpdateDialog transfer={grnTarget} onClose={() => setGrnTarget(null)} onSaved={() => { setGrnTarget(null); load(); }} />

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent data-testid="mcf-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete stock transfer {deleting?.invoice_number}?</AlertDialogTitle>
            <AlertDialogDescription>This removes the transfer and its SKU-wise Sent/GRN lines. The uploaded invoice file is kept. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="mcf-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive hover:bg-destructive/90" data-testid="mcf-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}
